import api from './api';

const isSupported = () =>
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

const toUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

const sendSubscription = async (subscription: PushSubscription) => {
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys) return;
    await api.post('/push/subscribe', {
        endpoint: json.endpoint,
        keys: json.keys,
    });
};

export const requestPushSubscription = async () => {
    if (!isSupported()) {
        return { ok: false, reason: 'unsupported' as const };
    }
    if (!window.isSecureContext && window.location.hostname !== 'localhost') {
        return { ok: false, reason: 'insecure' as const };
    }

    try {
        const { data } = await api.get('/push/public-key');
        const publicKey = data?.public_key as string | undefined;
        if (!publicKey) {
            return { ok: false, reason: 'missing_key' as const };
        }

        let permission = Notification.permission;
        if (permission !== 'granted') {
            permission = await Notification.requestPermission();
        }
        if (permission !== 'granted') {
            return { ok: false, reason: 'permission' as const, permission };
        }

        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (existing) {
            await sendSubscription(existing);
            return { ok: true, existing: true };
        }

        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: toUint8Array(publicKey),
        });
        await sendSubscription(subscription);
        return { ok: true, existing: false };
    } catch (err) {
        console.warn('Push subscription failed', err);
        return { ok: false, reason: 'error' as const };
    }
};

let ensurePushSubscriptionInFlight: Promise<void> | null = null;

const ensurePushSubscriptionInner = async () => {
    if (!isSupported()) return;
    if (!window.isSecureContext && window.location.hostname !== 'localhost') return;

    try {
        const { data } = await api.get('/push/public-key');
        const publicKey = data?.public_key as string | undefined;
        if (!publicKey) return;

        if (Notification.permission !== 'granted') return;

        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (existing) {
            await sendSubscription(existing);
            return;
        }

        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: toUint8Array(publicKey),
        });
        await sendSubscription(subscription);
    } catch (err) {
        console.warn('Push subscription failed', err);
    }
};

export const ensurePushSubscription = () => {
    if (!ensurePushSubscriptionInFlight) {
        ensurePushSubscriptionInFlight = ensurePushSubscriptionInner().finally(() => {
            ensurePushSubscriptionInFlight = null;
        });
    }
    return ensurePushSubscriptionInFlight;
};
